import Analytics from '@opencare/analytics';
import { get } from 'lodash';

import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Form } from 'opencare-crown';

import {
  DIMENSIONS,
  useWindowDimensions,
} from 'providers/WindowDimensionsProvider';
import { useAnalyticsService } from 'services/AnalyticsService';
import sanitizeString from 'utils/sanitizeString';
import scrollToRef from 'utils/scrollToRef';

import FloatingInput from '../../../../common/components/FloatingInput/FloatingInput';
import SingleSelect from '../../../../common/components/SingleSelect/SingleSelect';
import {
  AdditionalBlock,
  OptionBlock,
  PrimaryButton,
  Subtitle,
  Title,
} from '../../../../common/styled-components';
import { useFunnelStorage } from '../../../storage/Storage';
import { ButtonBlock, StepWrapper } from '../../../styled-components';
import { generateStepAnalytics, STEP_KEY } from '../../../utils';
import { asapOption } from '../../HowSoon/constants';
import { urgentIssues } from '../constants';

const analytics = new Analytics();

const Urgent = ({ position, moveToNextStep }) => {
  const storage = useFunnelStorage();
  const { layout } = useWindowDimensions();

  const analyticsService = useAnalyticsService();

  const refToAdditionalDetails = useRef();

  const [urgentForm] = Form.useForm();

  const savedReason = storage.get(STEP_KEY.REASON_DETAILS);

  const [selectedIssue, setSelectedIssue] = useState(
    get(savedReason, 'urgentReason') ? savedReason.urgentReason.title : null,
  );
  const [issueDetails, setIssueDetails] = useState(
    get(savedReason, 'urgentReason') ? savedReason.urgentReason.additional : '',
  );

  const getTextFieldLabel = (opt) => {
    switch (opt) {
      case 'Pain / Toothache':
        return layout === DIMENSIONS.MOBILE
          ? 'Please describe the pain'
          : "Please describe the pain you're experiencing";

      case 'Broken / Chipped Tooth':
        return layout === DIMENSIONS.MOBILE
          ? 'Please describe the damage'
          : 'Please describe the damage to the tooth';

      default:
        return 'Please describe your issue';
    }
  };

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_urgent',
      ...generateStepAnalytics(position, storage),
      ...analyticsService.analyticsData,
    });
  }, []);

  useEffect(() => {
    if (selectedIssue) {
      scrollToRef(refToAdditionalDetails);
    }
  }, [selectedIssue]);

  const selectIssue = (issue) => {
    setSelectedIssue(issue);
  };

  const complete = () => {
    const sanitizedDetails = sanitizeString(issueDetails);
    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_urgent',
      name: 'wizard',
      section: 'dentist-urgent',
      position: [
        urgentIssues.findIndex((option) => option.title === selectedIssue) + 1,
      ],
      ...generateStepAnalytics(position, storage, {
        response: `${selectedIssue}${
          sanitizedDetails ? ` - otherNote: ${sanitizedDetails}` : ''
        }`,
      }),
    });

    const previouslySaved = storage.get(STEP_KEY.REASON_DETAILS);

    const savedData = {
      ...previouslySaved,
      reason: 'Urgent Issue',
      reasonId: 'not-urgent',
      urgentReason: {
        title: selectedIssue,
        additional: sanitizedDetails,
      },
      treatments: null,
      other: null,
    };

    // Set questions we want to skip when patient has an urgency.
    const skipSteps = [
      {
        stepName: STEP_KEY.ATTITUDE,
        data: null,
      },
      {
        stepName: STEP_KEY.GOALS,
        data: null,
      },
      {
        stepName: STEP_KEY.HOW_SOON,
        data: asapOption.data,
      },
    ];

    storage.save(STEP_KEY.REASON_DETAILS, savedData, true, skipSteps);
    moveToNextStep();
  };

  return (
    <div data-testid="substep-urgent" id="substep-urgent">
      <Title id="title">What is the nature of your dental emergency?</Title>
      <Subtitle>
        Make a choice and we'll connect you with the right dentist, pronto.
      </Subtitle>
      <StepWrapper>
        <Form form={urgentForm} onFinish={complete} className="sensitive-data">
          <OptionBlock row={3}>
            {urgentIssues.map((issue, i) => (
              <SingleSelect
                key={issue.id}
                name="urgent"
                id={issue.id}
                title={issue.title}
                describedBy="title"
                onChange={() => selectIssue(issue.title, i)}
                selected={selectedIssue === issue.title}
              />
            ))}
          </OptionBlock>
          {selectedIssue
          && (
            <AdditionalBlock
              width="360px"
              ref={refToAdditionalDetails}
              tabIndex="-1"
            >
              <FloatingInput
                field="additional"
                form={urgentForm}
                label={getTextFieldLabel(selectedIssue)}
                initialValue={issueDetails}
                testId="additional"
                capitalize={false}
                onChange={(e) => setIssueDetails(e.target.value)}
                isTextarea
              />
            </AdditionalBlock>
          )}
          <ButtonBlock>
            <PrimaryButton
              type="submit"
              disabled={!selectedIssue || !issueDetails}
              data-testid="continue"
            >
              Continue
            </PrimaryButton>
          </ButtonBlock>
        </Form>
      </StepWrapper>
    </div>
  );
};

Urgent.defaultProps = {
  position: '',
};

Urgent.propTypes = {
  position: PropTypes.string,
  moveToNextStep: PropTypes.func.isRequired,
};

export default Urgent;
